import PropTypes from 'prop-types';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';

export default function LostReasonsBreakdown({ data, onReasonClick }) {
    const total = data.reduce((sum, item) => sum + item.count, 0);
    const maxCount = Math.max(...data.map((item) => item.count), 1);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Tooltip title="Reasons why leads were lost. Click a reason to see affected leads." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                            Lost Reasons
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                {total > 0 && (
                    <Chip
                        label={`${total} Total`}
                        size="small"
                        sx={{ bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 600, fontSize: '12px' }}
                    />
                )}
            </Box>

            {data.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No lost leads data available</Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {data.map((item, index) => {
                        const barWidth = (item.count / maxCount) * 100;
                        const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;

                        return (
                            <Box
                                key={item.reason || index}
                                onClick={() => onReasonClick && onReasonClick(item.reason)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    cursor: onReasonClick ? 'pointer' : 'default',
                                    borderRadius: '8px',
                                    p: 1,
                                    mx: -1,
                                    transition: 'all 0.2s ease',
                                    '&:hover': onReasonClick ? {
                                        bgcolor: '#fef2f2',
                                        transform: 'translateX(4px)',
                                    } : {},
                                }}
                            >
                                <Typography
                                    sx={{
                                        width: '180px',
                                        minWidth: '180px',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: '#334155',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={item.label}
                                >
                                    {item.label}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ height: '32px', bgcolor: '#fee2e2', borderRadius: '6px', overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                width: `${barWidth}%`,
                                                height: '100%',
                                                background: 'linear-gradient(90deg, #ef4444, #f87171)',
                                                borderRadius: '6px',
                                                transition: 'width 0.5s ease',
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ textAlign: 'right', minWidth: '80px' }}>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#ef4444' }}>
                                        {item.count}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
                                        {percentage}%
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

LostReasonsBreakdown.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            reason: PropTypes.string,
            label: PropTypes.string,
            count: PropTypes.number,
        })
    ),
    onReasonClick: PropTypes.func,
};

LostReasonsBreakdown.defaultProps = {
    data: [],
    onReasonClick: null,
};
